import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import type { MatchParticipantBrief, TournamentMatch } from "../lib/api";
import { DROPPED_NAME_SUFFIX, isDroppedParticipant } from "../lib/dropMatch";

/** Líneas "jugador + marcador por set" de la tarjeta de partido de la vista
 * jugador (Calendario/Draw). Compartidas con Resultados del admin (Eduardo
 * 2026-09-05: "las tablas de los partidos se ven mucho más pro que las de
 * vista admin"). */

export function participantName(participant: MatchParticipantBrief | null): string {
  return participant?.display_name?.trim() || "Por definir";
}

export function participantRankLabel(participant: MatchParticipantBrief | null): string | null {
  const rank = participant?.initial_rank ?? participant?.tournament_rank ?? participant?.current_rank ?? null;
  return typeof rank === "number" ? `#${rank}` : null;
}

export interface ParsedScoreSides {
  player1: string[];
  player2: string[];
  /** Puntos del tiebreak por set y lado (superíndice tipo ATP: 7⁷ / 6⁵); null sin tiebreak. */
  tiebreaks: { player1: (number | null)[]; player2: (number | null)[] };
}

/** Puntos de cada lado en un tiebreak a partir de los del perdedor ("7-6(5)"):
 * el ganador hace 7 o, si el perdedor pasó de 5, dos más que él. */
export function tiebreakPointsForSides(
  loserPoints: number | null | undefined,
  player1Won: boolean,
): [number | null, number | null] {
  if (loserPoints == null || !Number.isFinite(loserPoints)) return [null, null];
  const winnerPoints = loserPoints >= 5 ? loserPoints + 2 : 7;
  return player1Won ? [winnerPoints, loserPoints] : [loserPoints, winnerPoints];
}

/** Games por set de cada lado; con score_text libre ("6-4 7-6(5)") lo parsea. */
export function parsedScoreSides(match: TournamentMatch): ParsedScoreSides {
  const empty: ParsedScoreSides = { player1: [], player2: [], tiebreaks: { player1: [], player2: [] } };
  if (match.score_sets?.length) {
    const result: ParsedScoreSides = { player1: [], player2: [], tiebreaks: { player1: [], player2: [] } };
    for (const set of match.score_sets) {
      result.player1.push(String(set.player1_games));
      result.player2.push(String(set.player2_games));
      // Un súper tiebreak ya trae sus puntos como "games" (10-8): sin superíndice.
      const [tb1, tb2] = set.is_super_tiebreak
        ? [null, null]
        : tiebreakPointsForSides(set.tiebreak_points, set.player1_games > set.player2_games);
      result.tiebreaks.player1.push(tb1);
      result.tiebreaks.player2.push(tb2);
    }
    return result;
  }
  const scoreText = match.score_text?.trim();
  if (!scoreText || scoreText.toUpperCase() === "BYE") return empty;
  const result: ParsedScoreSides = { player1: [], player2: [], tiebreaks: { player1: [], player2: [] } };
  for (const part of scoreText.split(/\s+/)) {
    const found = part.match(/^(\d+)\s*[-–:/]\s*(\d+)(?:\((\d+)(?:-(\d+))?\))?$/);
    if (!found) continue;
    result.player1.push(found[1]);
    result.player2.push(found[2]);
    const player1Won = Number(found[1]) > Number(found[2]);
    let tb1: number | null = null;
    let tb2: number | null = null;
    if (found[3] != null && found[4] != null) {
      // "(8-6)": ganador-perdedor del tiebreak, ya explícitos.
      const winnerPoints = Number(found[3]);
      const loserPoints = Number(found[4]);
      [tb1, tb2] = player1Won ? [winnerPoints, loserPoints] : [loserPoints, winnerPoints];
    } else {
      // "(5)": solo los puntos del perdedor.
      [tb1, tb2] = tiebreakPointsForSides(found[3] != null ? Number(found[3]) : null, player1Won);
    }
    result.tiebreaks.player1.push(tb1);
    result.tiebreaks.player2.push(tb2);
  }
  return result;
}

interface MatchPlayerLineProps {
  participant: MatchParticipantBrief | null;
  score: string[];
  winner: boolean;
  bye?: boolean;
  /** Link del nombre (perfil público del jugador, detalle admin…); sin href el nombre va en texto. */
  href?: string | null;
  /** Puntos del tiebreak por set (superíndice); misma longitud que `score`. */
  tiebreaks?: (number | null)[];
  /** Nombre ya armado (p. ej. los dos integrantes de una línea de dobles con
   * su link cada uno); sustituye a `participant` para el rótulo. */
  nameNode?: ReactNode;
}

export function MatchPlayerLine({
  participant,
  score,
  winner,
  bye = false,
  href = null,
  tiebreaks,
  nameNode,
}: MatchPlayerLineProps) {
  const rank = nameNode ? null : participantRankLabel(participant);
  const name = participantName(participant);
  // Eduardo 2026-10-05 (torneo 69): quien se dio de baja sigue en el renglón
  // (es historial), marcado «Vicky Cattan Michan · baja».
  const dropped = !bye && !nameNode && isDroppedParticipant(participant);
  // 4-5 sets: en celular el nombre pasa a su propio renglón arriba del marcador
  // (si no, la columna del nombre se queda en 40 px y se parte letra por letra).
  const manySets = score.length >= 4;
  return (
    <div className={`player-games-competitor${winner ? " is-winner" : ""}${bye ? " is-bye" : ""}${dropped ? " is-dropped" : ""}${manySets ? " player-games-competitor--many-sets" : ""}`}>
      <span className="player-games-competitor__identity">
        {rank && <span className="player-games-competitor__rank">{rank}</span>}
        {bye ? (
          <strong>BYE</strong>
        ) : nameNode ? (
          <strong>{nameNode}</strong>
        ) : href ? (
          <Link className="player-name-action" to={href}>
            <strong>{name}</strong>
          </Link>
        ) : (
          <strong>{name}</strong>
        )}
        {dropped ? <small className="player-games-competitor__drop">{DROPPED_NAME_SUFFIX}</small> : null}
      </span>
      {score.length > 0 && (
        <span className="player-games-competitor__score" aria-label="Marcador">
          {score.map((value, idx) => (
            <span key={`${value}-${idx}`}>
              {value}
              {tiebreaks?.[idx] != null ? <sup>{tiebreaks[idx]}</sup> : null}
            </span>
          ))}
        </span>
      )}
    </div>
  );
}

/** Nombres de un lado de una línea de serie ("Ana / Beto"), cada uno con su
 * link al perfil cuando hay `hrefForProfile`. */
export function lineSideNames(
  line: TournamentMatch,
  side: "team1" | "team2",
  hrefForProfile?: (profileId: number) => string | null,
): ReactNode {
  const players = line.line_players?.[side] ?? [];
  if (players.length === 0) return "Por definir";
  return players.map((player, index) => {
    const href = hrefForProfile ? hrefForProfile(player.id) : null;
    return (
      <span key={player.id}>
        {index > 0 ? " / " : ""}
        {href ? (
          <Link className="player-name-action" to={href}>
            {player.name}
          </Link>
        ) : (
          player.name
        )}
      </span>
    );
  });
}

/** Marcador de una LÍNEA de serie (singles/dobles) con el mismo formato de
 * dos renglones que el partido: nombre + games por set, tiebreak en
 * superíndice, ganador resaltado (Eduardo 2026-09-17: "formato tipo ATP"). */
export function LineCompetitorLines({
  line,
  hrefForProfile,
}: {
  line: TournamentMatch;
  hrefForProfile?: (profileId: number) => string | null;
}) {
  const sides = parsedScoreSides(line);
  const winner1 = line.winner_participant_id != null && line.winner_participant_id === line.player1_participant_id;
  const winner2 = line.winner_participant_id != null && line.winner_participant_id === line.player2_participant_id;
  return (
    <div className="player-games-match-card__players">
      <MatchPlayerLine
        participant={null}
        nameNode={lineSideNames(line, "team1", hrefForProfile)}
        score={sides.player1}
        tiebreaks={sides.tiebreaks.player1}
        winner={winner1}
      />
      <MatchPlayerLine
        participant={null}
        nameNode={lineSideNames(line, "team2", hrefForProfile)}
        score={sides.player2}
        tiebreaks={sides.tiebreaks.player2}
        winner={winner2}
      />
    </div>
  );
}

/** Las dos líneas del partido (arriba jugador 1, abajo jugador 2). */
export function MatchCompetitorLines({
  match,
  hrefFor,
}: {
  match: TournamentMatch;
  hrefFor?: (participant: MatchParticipantBrief) => string | null;
}) {
  const sides = parsedScoreSides(match);
  const linkFor = (participant: MatchParticipantBrief | null) =>
    participant && hrefFor ? hrefFor(participant) : null;
  return (
    <div className="player-games-match-card__players">
      <MatchPlayerLine
        participant={match.player1}
        score={sides.player1}
        tiebreaks={sides.tiebreaks.player1}
        winner={match.winner_participant_id != null && match.winner_participant_id === match.player1_participant_id}
        href={linkFor(match.player1)}
      />
      <MatchPlayerLine
        participant={match.player2}
        score={sides.player2}
        tiebreaks={sides.tiebreaks.player2}
        winner={match.winner_participant_id != null && match.winner_participant_id === match.player2_participant_id}
        bye={match.status === "bye"}
        href={linkFor(match.player2)}
      />
    </div>
  );
}
